import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, Observable, from } from 'rxjs';
import { catchError, map, mergeMap, retryWhen, take, delay, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { IdbCatalogName } from 'src/app/idb/idb-catalog-name.enum';

import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { PlantNetworkStatus } from './plant-network-status.models';
import * as act from './plant-network-status.actions';
import { PlantListInterface } from 'src/app/pages/record/interfaces';
import { PlantManagementService } from '@shared/services/plant/plant-management.service';
import { selectPlantByCode, selectPlanstByCodes } from './plant-network-status.selectors';
import { PlantNetworkStatusFacade } from './plant-network-status.facade';

@Injectable()
export class PlantNetworkEffects {
  constructor(
    private actions$: Actions,
    private idb: ManageIdbService,
    private pm: PlantManagementService,
    private store: Store,
    private plantFacade: PlantNetworkStatusFacade,
  ) {}

  /**
   * Effect to load plants from IndexedDB
   */
  fetchPlants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(act.fetchPlants),
      mergeMap(() =>
        this.getPlantsNetworkStatus().pipe(
          map((plants) => {
            return act.fetchPlantsSuccess({ plants });
          }),
          catchError((e) => {
            console.error('Error fetching plants:', e);
            return of(
              act.fetchPlantsFailure({
                payload: e.message || 'Error fetching plants',
              }),
            );
          }),
        ),
      ),
    ),
  );

  /**
   * Effect to refresh online/offline status of a plant in IndexedDB
   */
  updatePlantStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(act.updatePlantStatus),
      mergeMap(({ payload }) => {
        return this.updatePlantInIndexedDB(payload.PlantCode, payload.IsOnline).pipe(
          map(() =>
            act.updatePlantStatusSuccess({ payload: { PlantCode: payload.PlantCode, IsOnline: payload.IsOnline } }),
          ),
          catchError((e) =>
            of(
              act.updatePlantStatusFailure({
                payload: `Error updating plant ${payload.PlantCode}: ${e.message}`,
              }),
            ),
          ),
        );
      }),
    ),
  );

  /**
   * Effect to get the latest status of a plant
   */
  getLatestPlantNetworkStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(act.getLatestPlantNetworkStatus),
      mergeMap(({ plantCode }) => {
        return this.pm.GetPlantNetworkStatus(plantCode).pipe(
          mergeMap((plantDb) => {
            // Use mergeMap to safely combine both observables
            return this.store.select(selectPlantByCode(plantCode)).pipe(
              take(1), // Important: take only the first value and complete the subscription
              map((selectedPlant) => {
                if (!selectedPlant) {
                  this.plantFacade.sendLatestPlantStatus(plantCode, plantDb?.IsOnline ?? true, 0);
                  return act.getLatestPlantNetworkStatusFailure({
                    plantCode,
                    error: 'Error: Plant not found in store',
                  });
                }
                if (selectedPlant) {
                  // Update the plant status in the store
                  this.plantFacade.updatePlantStatus(plantCode, plantDb.IsOnline);

                  // Update the status in the facade so that services can receive it
                  this.plantFacade.sendLatestPlantStatus(plantCode, plantDb.IsOnline, selectedPlant.TimeDifference);

                  return act.getLatestPlantNetworkStatusSuccess({
                    plantCode,
                    plant: {
                      ...plantDb,
                      TimeDifference: selectedPlant.TimeDifference,
                      PlantDescription: selectedPlant.PlantDescription,
                    },
                  });
                }
              }),
            );
          }),
          catchError((e) => {
            this.plantFacade.sendLatestPlantStatus(plantCode, true, 0);
            return of(
              act.getLatestPlantNetworkStatusFailure({
                plantCode,
                error: e.message || 'Error fetching plant status',
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Read network status plant from IndexedDB
   */
  private getPlantsNetworkStatus(): Observable<PlantNetworkStatus[]> {
    return from(this.idb.select(IdbCatalogName.Plants)).pipe(
      retryWhen((e) => e.pipe(delay(300), take(3))),
      take(1),
      map((allPlants: PlantListInterface[]) => {
        // Filter plants
        const filteredPlants: PlantNetworkStatus[] = (allPlants ?? []).map((p) => ({
          PlantCode: p.PlantCode,
          IsOnline: p.IsOnline,
          TimeDifference: p.TimeDifference,
          PlantDescription: p.PlantDesc,
        }));

        return filteredPlants;
      }),
      catchError((e) => {
        console.error('Error fetching plants network status:', e);
        return of([]);
      }),
    );
  }

  /**
   * Update plant status in IndexedDB
   */
  private updatePlantInIndexedDB(plantCode: string, isOnline: boolean): Observable<any> {
    return from(this.idb.updatePlantProperties(plantCode, ['IsOnline'], [isOnline])).pipe(
      take(1),
      catchError((e) => {
        console.error(`Error updating plant ${plantCode} status:`, e);
        throw e;
      }),
    );
  }

  /**
   * Effect to get the latest status of plants array
   */
  getLatestPlantsNetworkStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(act.getLatestPlantsNetworkStatus),
      mergeMap((params) => {
        const plantCodes = params.plantCodes;
        return this.pm.GetPlantsNetworkStatus(plantCodes.toString()).pipe(
          mergeMap((plantsDb) => {
            // Use mergeMap to safely combine both observables
            return this.store.select(selectPlanstByCodes(plantCodes)).pipe(
              take(1), // Important: take only the first value and complete the subscription
              map((selectedPlants) => {
                const plantsUpdated: PlantNetworkStatus[] = [];
                plantCodes.forEach((plantCode) => {
                  const selectedPlant = selectedPlants.find((plant) => plant.PlantCode === plantCode);
                  const plantDb = plantsDb.find((plant) => plant.PlantCode === plantCode);
                  if (!selectedPlant) {
                    this.plantFacade.sendLatestPlantStatus(plantCode, plantDb?.IsOnline ?? true, 0);
                  }
                  if (selectedPlant) {
                    // Update the plant status in the store
                    this.plantFacade.updatePlantStatus(plantCode, plantDb.IsOnline);

                    // Update the status in the facade so that services can receive it
                    this.plantFacade.sendLatestPlantStatus(plantCode, plantDb.IsOnline, selectedPlant.TimeDifference);
                    plantsUpdated.push({
                      ...plantDb,
                      TimeDifference: selectedPlant.TimeDifference,
                      PlantDescription: selectedPlant.PlantDescription,
                    });
                  }
                });

                return act.getLatestPlantsNetworkStatusSuccess({ plantCodes: plantCodes, plants: plantsUpdated });
              }),
            );
          }),
          catchError((e) => {
            plantCodes.forEach((plantCode) => {
              this.plantFacade.sendLatestPlantStatus(plantCode, true, 0);
            });

            return of(
              act.getLatestPlantsNetworkStatusFailure({
                plantCodes,
                error: e.message || 'Error fetching plant status',
              }),
            );
          }),
        );
      }),
    ),
  );
}
